import { Component, NgZone, ChangeDetectorRef, ChangeDetectionStrategy, ElementRef, ViewEncapsulation, Input } from '@angular/core';
import { DateTime } from '@common/data-models/complex-types/date-time';
import { EnpsControl } from '@common/controls/enps-control/enps-control';
import { TimeoutQueue } from '@common/services/timeout-queue';
import { AppService } from '@suite/app.service';
import { GlobalTablesService } from "@common/services/global-tables-service";
import { VisualStateService } from '@common/services/visual-state-service';
import { LanguageSettings } from '@common/services/language-settings';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter, MAT_MOMENT_DATE_FORMATS } from '@angular/material-moment-adapter';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import moment, { Moment } from 'moment-timezone';
import { FormControl, Validators } from '@angular/forms';
import { take } from 'rxjs/operators';

@Component({
    selector: 'date-picker',
    templateUrl: './date-picker.html',
    // changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./date-picker.scss'],
    host: {
        class: 'st-date-picker'

    },
    providers: [
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    ]
})

export class DatePicker extends EnpsControl<DateTime> {

    internalFormControl: FormControl<Moment> = new FormControl(null);


    private _min: DateTime = new DateTime();
    @Input()
    get min(): DateTime {
        return this._min;
    }
    set min(value: DateTime) {
        if (value) {
            this._min = value;
        }
    }

    get minDate(): Moment {
        return this.min?.moment || null;
    }

    constructor(
        public appService: AppService,
        ngZone: NgZone,
        changeDetectorRef: ChangeDetectorRef,
        timeoutQueue: TimeoutQueue,
        globalTablesService: GlobalTablesService,
        public visualStateService: VisualStateService,
        public languageSettings: LanguageSettings,
        elmRef: ElementRef,
        private dateAdapter: DateAdapter<Moment>,
    ) {
        super(appService, ngZone, changeDetectorRef, timeoutQueue, globalTablesService, elmRef);
        dateAdapter.setLocale(this.locale);
    }

    dateChange(value: MatDatepickerInputEvent<Moment>) {
        if (value.value) {
            const newDate = this.value.get() || new Date();
            newDate?.setFullYear(value.value.toDate().getFullYear());
            newDate?.setMonth(value.value.toDate().getMonth(), value.value.toDate().getDate());
            const newDateTime = new DateTime(newDate);
            this._calculateNgTimeOffset(newDate);
            this.changed.emit(newDateTime);
        } else {
            this.value.set(null);
            this.changed.emit(this.value);
        }
    }

    public clearDate() {
        this.value.set(null);
        this.internalFormControl.setValue(null);
        this.changed.emit(this.value);
    }

    public getDatePicker(picker: any) {
        setTimeout(() => {
            picker._componentRef?.instance?._calendar.monthView.selectedChange.pipe(take(1)).subscribe((res: Moment) => {
                const newDate = this.value.get() || new Date();
                newDate?.setFullYear(res.toDate().getFullYear());
                newDate?.setMonth(res.toDate().getMonth(), res.toDate().getDate());
                const newDateTime = new DateTime(newDate);
                this._calculateNgTimeOffset(newDate);
                this.changed.emit(newDateTime);
                picker.close()
            })
        }, 0)
    }

    get dateValue(): Moment {
        if (this.value?.get()?.getFullYear() == 1800) {
            return null;
        }
        return this.value.moment;
    }


    _syncValueIfNecessary() {
        this.internalFormControl.setValue(this.dateValue);
    }

    _addValidatorIfNecessary() {
        this.internalFormControl.addValidators(Validators.required);
        this.internalFormControl.markAllAsTouched();
    }

    _setDisabledState(isDisabled: boolean) {
        if (isDisabled) {
            this.internalFormControl.disable();
        } else {
            this.internalFormControl.enable();
        }
    }

    private _calculateNgTimeOffset(date: Date) {
        const offset = this.visualStateService.ConfigSettings.NGTimeOffset;
        if (offset) {
            const chunks = offset.split(':');
            const hours = parseInt(chunks[0]);
            const minutes = parseInt(chunks[1]);
            date.setUTCHours(hours);
            date.setUTCMinutes(minutes);
            date.setUTCSeconds(0);
            date.setUTCMilliseconds(0);
        }
    }
}
